import { useState, useEffect } from 'react';
import { SingleValue } from 'react-select';
import {
	generateOption,
	getDefaultMaximumDate,
	getDefaultMinimumDate,
	getOptionsSuccession,
	getOptionsYearsSuccession,
	stringTwoDigitNumber
} from '../utils/OptionsGetters';
import { fromDateToFormat, fromFormatToDate } from '../utils/FormatRetrievers';
import {
	DateSelectorOptions,
	INPUT_DATE_SELECT_FORMAT,
	InputSelectOption
} from '../types/InputDateSelectTransformerTypes';

export function useDateSelector(
	props: DateSelectorOptions | undefined = { format: INPUT_DATE_SELECT_FORMAT.ISO_STRING }
) {
	const { placeholders, value, onChange, format } = props;

	const minDate = props.minDate ?? getDefaultMinimumDate(format);
	const maxDate = props.maxDate ?? getDefaultMaximumDate(format);
	const defaultDayValue = format === INPUT_DATE_SELECT_FORMAT.CREDIT_CARD ? 1 : 0;
	const DEFAULT_DAY_SELECTED = { label: placeholders?.day ?? 'dd', value: defaultDayValue };
	const DEFAULT_MONTH_SELECTED = { label: placeholders?.month ?? 'mm', value: 0 };
	const DEFAULT_YEAR_SELECTED = { label: placeholders?.year ?? 'yyyy', value: 0 };

	/**
	 * Day input state
	 */
	const [daysOptions, setDaysOptions] = useState<InputSelectOption[]>(getOptionsSuccession(31));
	const [selectedDay, setSelectedDay] = useState<InputSelectOption>(DEFAULT_DAY_SELECTED);

	/**
	 * Month input state. If the max date and min date are the same year. Limit the possible months to select.
	 */
	const endMonth = maxDate.getFullYear() === minDate.getFullYear() ? maxDate.getMonth() : 11;
	const startMonth = maxDate.getFullYear() === minDate.getFullYear() ? minDate.getMonth() : 0;
	const [selectedMonth, setSelectedMonth] = useState<InputSelectOption>(DEFAULT_MONTH_SELECTED);
	const [monthOptions, setMonthOptions] = useState<InputSelectOption[]>(
		getOptionsSuccession(endMonth - startMonth + 1, startMonth)
	);

	/**
	 * Year input state
	 */
	const [selectedYear, setSelectedYear] = useState<InputSelectOption>(DEFAULT_YEAR_SELECTED);
	const [yearOptions, setYearOptions] = useState<InputSelectOption[]>(
		getOptionsYearsSuccession(maxDate.getFullYear() - minDate.getFullYear() + 1, maxDate.getFullYear())
	);

	/**
	 * When the placeholders change, update the default values of the selects
	 */
	useEffect(() => {
		if (!selectedDay.value) {
			setSelectedDay(DEFAULT_DAY_SELECTED);
		}
		if (!selectedMonth.value) {
			setSelectedMonth(DEFAULT_MONTH_SELECTED);
		}
		if (!selectedYear.value) {
			setSelectedYear(DEFAULT_YEAR_SELECTED);
		}
	}, [placeholders]);

	/**
	 * Function utility to set all selects only from one date
	 */
	const setSelectInputFromDate = (value: Date) => {
		const day = value.getDate();
		setSelectedDay({ value: day, label: stringTwoDigitNumber(day) });
		const month = value.getMonth() + 1;
		setSelectedMonth({ value: month, label: stringTwoDigitNumber(month) });
		const year = value.getFullYear();
		setSelectedYear({ value: year, label: year.toString() });
	};

	/**
	 *	When the value of the input changes by an external prop, update the value of the selects accordingly
	 */
	useEffect(() => {
		if (value) {
			const dateValue = fromFormatToDate(value, format, yearOptions);
			setSelectInputFromDate(dateValue);
		} else {
			setSelectedDay(DEFAULT_DAY_SELECTED);
			setSelectedMonth(DEFAULT_MONTH_SELECTED);
			setSelectedYear(DEFAULT_YEAR_SELECTED);
		}
	}, [value]);

	/**
	 *	When the value of the max date and min date changes, update the year options and change year and validate it if needed.
	 */
	useEffect(() => {
		setYearOptions(getOptionsYearsSuccession(maxDate.getFullYear() - minDate.getFullYear() + 1, maxDate.getFullYear()));
		if (selectedYear.value) handleYearChange(generateOption(selectedYear.value));
	}, [maxDate.getFullYear(), minDate.getFullYear()]);

	/**
	 * When new date is selected from inputs, trigger onChange event callback
	 */
	useEffect(() => {
		if (selectedDay.value && selectedMonth.value && selectedYear.value) {
			const date = new Date(selectedYear.value, selectedMonth.value - 1, selectedDay.value);
			date.setFullYear(selectedYear.value);
			date.setMonth(selectedMonth.value - 1);
			date.setDate(selectedDay.value);
			onChange?.(fromDateToFormat(date, format) as (Date & string) | undefined);
		}
	}, [selectedDay.value, selectedMonth.value, selectedYear.value]);

	/**
	 * Update input select day with the value or options depending of the newDate and the maximum and minimum date available.
	 */
	const updateSelectsDayWithDate = (newDate: Date, year: number, month: number) => {
		if (newDate > maxDate) {
			setSelectedDay(generateOption(maxDate.getDate()));
			setDaysOptions(getOptionsSuccession(maxDate.getDate()));
		} else if (newDate < minDate) {
			setSelectedDay(generateOption(minDate.getDate()));
			const daysInMonth = new Date(year, month - 1, 0).getDate();
			setDaysOptions(getOptionsSuccession(daysInMonth - minDate.getDate(), minDate.getDate() - 1));
		} else if (newDate.getFullYear() === maxDate.getFullYear() && newDate.getMonth() === maxDate.getMonth()) {
			setDaysOptions(getOptionsSuccession(maxDate.getDate()));
		} else if (newDate.getFullYear() === minDate.getFullYear() && newDate.getMonth() === minDate.getMonth()) {
			const daysInMonth = new Date(year, month - 1, 0).getDate();
			setDaysOptions(getOptionsSuccession(daysInMonth - minDate.getDate(), minDate.getDate() - 1));
		}
	};

	/**
	 * Modify day input depending of the month, this function is called when the month has changed.
	 */
	const modifyDateByMonthChange = (month: number, updatedYear?: number) => {
		const year = updatedYear ?? selectedYear.value;

		// With month change, update day options depending of month and selected day if month has less days
		const yearToDaysInMonth = year ?? new Date().getFullYear();
		const lastDayMonth = new Date(yearToDaysInMonth, month, 0).getDate();

		setDaysOptions(getOptionsSuccession(lastDayMonth));
		if (selectedDay.value && selectedDay.value > lastDayMonth) {
			setSelectedDay(generateOption(lastDayMonth));
		}

		// With month change date of input greater than max date or less than min date, update day and update options
		const allDataFilled = year && month;
		if (selectedDay.value && allDataFilled) {
			const newDate = new Date(year, month - 1, selectedDay.value);
			updateSelectsDayWithDate(newDate, year, month);
		}
	};

	/**
	 * On day input change
	 */
	const handleDayChange = (selectedOption: SingleValue<InputSelectOption>) => {
		if (selectedOption) {
			setSelectedDay(selectedOption);
		}
	};

	/**
	 * On month input change
	 */
	const handleMonthChange = (selectedOption: SingleValue<InputSelectOption>) => {
		if (selectedOption) {
			const newMonth = selectedOption.value;

			if (newMonth) {
				// Update month selected
				setSelectedMonth(generateOption(newMonth));
				// Update day with month change
				modifyDateByMonthChange(newMonth);
			}
		}
	};

	/**
	 * On year input change
	 */
	const updateMaxYear = (newYear: number) => {
		setMonthOptions(getOptionsSuccession(maxDate.getMonth() + 1));
		if (selectedMonth.value && selectedMonth.value >= maxDate.getMonth() + 1) {
			setSelectedMonth(generateOption(maxDate.getMonth() + 1));
			modifyDateByMonthChange(maxDate.getMonth() + 1, newYear);
		}
	};

	const updateMinYear = (newYear: number) => {
		setMonthOptions(getOptionsSuccession(12 - minDate.getMonth(), minDate.getMonth()));
		if (selectedMonth.value && selectedMonth.value <= minDate.getMonth() + 1) {
			setSelectedMonth(generateOption(minDate.getMonth() + 1));
			modifyDateByMonthChange(minDate.getMonth() + 1, newYear);
		}
	};

	const handleYearChange = (selectedOption: SingleValue<InputSelectOption>) => {
		if (selectedOption) {
			// Update year selected
			setSelectedYear(selectedOption);

			const newYear = selectedOption.value;
			// If with year change, year equal to max date or min date year, update month options to available options and update selected month if date is greater than selected
			if (newYear === maxDate.getFullYear()) {
				updateMaxYear(newYear);
			} else if (newYear === minDate.getFullYear()) {
				updateMinYear(newYear);
			} else if (newYear && selectedMonth.value) {
				const yearToDaysInMonth = newYear;
				const lastDayMonth = new Date(yearToDaysInMonth, selectedMonth.value, 0).getDate();
				setDaysOptions(getOptionsSuccession(lastDayMonth));
				setMonthOptions(getOptionsSuccession(12));
			} else {
				setMonthOptions(getOptionsSuccession(12));
			}
		}
	};

	return {
		daysOptions,
		monthOptions,
		yearOptions,
		selectedDay,
		selectedMonth,
		selectedYear,
		handleDayChange,
		handleMonthChange,
		handleYearChange
	};
}
